import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import { h2, h5, paragraphLarge, paragraphSmall } from '@helpers/typography';
import CardGrid from '@stories/Components/CardGrid/CardGrid';
import { cardWidth } from '@stories/Components/CardGrid/CardGrid.styles';
import FormStyles from '@stories/Components/Misc/Forms/Form.styles';
import SectionWrapper, { ContentType, } from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import TitleAndIntroductionStyles from '@stories/Components/Misc/TitleAndIntroduction/TitleAndIntroduction.styles';
import styled from 'styled-components';
const Container = styled(SectionWrapper) `
  --backgroundColour: ${brand.green.light};
  --featureColour: ${brand.green.dark};
  --paddingX: ${fluid(18, 26)};
  --paddingY: 26px;

  background-color: var(--backgroundColour);
  border-radius: 0 0 ${fluid(25, 50)} 0;

  @media ${from(Device.Tablet)} {
    border-radius: 0 0 ${fluid(25, 50)} ${fluid(25, 50)};
  }

  &[data-theme=${ContentType.Savings}] {
    --backgroundColour: ${brand.blue.light};
    --featureColour: ${brand.blue.dark};
  }

  ${TitleAndIntroductionStyles.Container} {
    margin-bottom: ${fluid(24, 48)};
  }

  ${TitleAndIntroductionStyles.Introduction} {
    margin-bottom: 0;
  }
`;
const Wrapper = styled.div ``;
const TopArea = styled.div ``;
const Tag = styled.div `
  ${paragraphSmall};
  ${fonts.proximaNova.semiBold};

  display: inline-block;
  color: ${brand.global.white};
  background-color: ${brand.blue.dark};
  border-radius: 10000000px;
  padding: 8px 12px;
  margin-bottom: 12px;
`;
const Title = styled.h1 `
  ${h2};
  margin-bottom: 18px;
  max-width: 30ch;
`;
const Introduction = styled.p `
  ${paragraphLarge};
  ${fonts.proximaNova.medium};
  margin-bottom: ${fluid(22, 38)};
  max-width: 70ch;
`;
const Features = styled(CardGrid) `
  --maxWidth: 33%;
  --rowItems: 2;
  --alignment: flex-start;

  grid-row: 3;
  grid-column: 1;
  width: 100%;
  margin-top: ${fluid(16, 28)};

  &[data-single-col='true'] {
    --grow: 0;

    @media ${until(Device.TabletSmall)} {
      --rowItems: 1;
      --grow: 1;
    }
  }

  @media ${from(Device.TabletSmall)} {
    --rowItems: 3 !important; // Important required to prevent CLS specificity issue
  }

  @media ${from(Device.Tablet)} {
    --rowItems: 4 !important;
  }

  @media ${from(Device.TabletLarge)} {
    --rowItems: 5 !important;

    &[data-single-col='true'] {
      --rowItems: 4 !important;
    }
  }

  @media ${from(Device.Desktop)} {
    --rowItems: 6 !important;

    &[data-single-col='true'] {
      --rowItems: 4 !important;
    }
  }
`;
const Info = styled(FormStyles.Info) `
  grid-column: 2;
  margin: 2px 0 0;
`;
const InfoButton = styled(FormStyles.InfoButton) ``;
const Feature = styled.div `
  ${cardWidth};

  display: grid;
  grid-template-columns: 1fr min-content;
  column-gap: ${fluid(8, 16)};
  background-color: ${brand.global.white};
  padding: 4px 12px;
  border-radius: 10px 10px 10px 0;
  z-index: 1;

  /* Larger z-index for feature displaying a tooltip to ensure info buttons don't overlap the tooltip when features stack */
  &:has(${Info}:hover, ${Info}:focus-visible, ${Info}:active) {
    z-index: 2;
  }
`;
const FeatureTitle = styled.h2 `
  ${paragraphSmall};
  ${fonts.proximaNova.bold};

  grid-column: 1;
  margin: 0;
`;
const FeatureContent = styled.p `
  ${h5};

  grid-column: 1 / span 2;
  margin: 0;
  color: var(--featureColour);
  margin-bottom: ${fluid(0, 24)};
`;
const Tooltip = styled(FormStyles.Tooltip) `
  --width: min(50vw, calc(var(--siteWidth) * 0.5));

  width: calc(var(--width) - (var(--paddingX) * 2) - var(--sitePadding));
  z-index: 1;

  @media ${from(Device.TabletSmall)} {
    --width: min(33vw, calc(var(--siteWidth) * 0.33));
  }

  @media ${from(Device.Tablet)} {
    --width: min(27.5vw, calc(var(--siteWidth) * 0.275));
  }

  @media ${from(Device.TabletLarge)} {
    --width: min(25vw, calc(var(--siteWidth) * 0.25));
  }
`;
const BottomArea = styled.div `
  margin-top: ${fluid(22, 22)};
`;
const SummaryBullets = styled.ul `
  display: flex;
  flex-direction: column;
  margin-bottom: ${fluid(24, 29)};
  gap: 8px;
  margin-left: 14px;

  @media ${from(Device.Tablet)} {
    flex-direction: row;
    margin-left: 0;
    gap: 16px 33px;
  }

  li {
    ${paragraphSmall};
    ${fonts.proximaNova.semiBold};
    position: relative;
    margin-bottom: 0;

    &:before {
      content: '';
      position: absolute;
      left: -14px;
      top: 50%;
      transform: translateY(-50%);
      width: ${fluid(6, 7)};
      height: ${fluid(6, 7)};
      background-color: var(--featureColour);
      border-radius: 100%;

      @media ${from(Device.Tablet)} {
        left: -19px;
      }
    }

    &:first-of-type {
      &:before {
        @media ${from(Device.Tablet)} {
          content: none;
        }
      }
    }
  }
`;
const ProductHerotyles = {
    BottomArea,
    Container,
    Feature,
    FeatureContent,
    Features,
    FeatureTitle,
    Info,
    Introduction,
    SummaryBullets,
    Tag,
    Title,
    Tooltip,
    TopArea,
    Wrapper,
    InfoButton,
};
export default ProductHerotyles;
